import React from "react";
import {
	Box,
	Typography,
	Button,
	Card,
	CardContent,
	Chip,
	Modal,
	IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
	pricingConfig,
	getText,
	getCurrentVariant,
	getPaywallTrigger,
} from "../../utils/pricingHelpers";

const PaywallModal = ({ open, onClose, triggerAction, variant }) => {
	const currentVariant = variant || getCurrentVariant();
	const { paywallModal } = pricingConfig;

	// Buscar el trigger correspondiente
	const trigger = triggerAction ? getPaywallTrigger(triggerAction) : null;

	if (!open) return null;

	return (
		<Modal
			open={open}
			onClose={onClose}
			sx={{
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				p: 2,
			}}
		>
			<Box
				sx={{
					backgroundColor: "white",
					borderRadius: 3,
					maxWidth: 700,
					width: "100%",
					maxHeight: "90vh",
					overflow: "auto",
					position: "relative",
					boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
				}}
			>
				{/* Close Button */}
				<IconButton
					onClick={onClose}
					sx={{
						position: "absolute",
						top: 16,
						right: 16,
						zIndex: 1,
					}}
				>
					<CloseIcon />
				</IconButton>

				<Box sx={{ p: 4 }}>
					{/* Trigger-specific content */}
					{trigger && (
						<Box sx={{ mb: 3, textAlign: "center" }}>
							<Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>
								{getText(trigger.title, currentVariant)}
							</Typography>
							{trigger.subtitle && (
								<Typography
									variant="body2"
									color="text.secondary"
									sx={{ mb: 2 }}
								>
									{getText(trigger.subtitle, currentVariant)}
								</Typography>
							)}
							<Typography variant="body1" sx={{ mb: 2 }}>
								{getText(trigger.message, currentVariant)}
							</Typography>
						</Box>
					)}

					{/* Modal Header */}
					<Box sx={{ textAlign: "center", mb: 4 }}>
						<Typography variant="h4" sx={{ mb: 1, fontWeight: 700 }}>
							{getText(paywallModal.title, currentVariant)}
						</Typography>
						<Typography variant="body1" color="text.secondary">
							{getText(paywallModal.subtitle, currentVariant)}
						</Typography>
					</Box>

					{/* Plans */}
					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: {
								xs: "1fr",
								sm: "repeat(2, 1fr)",
							},
							gap: 3,
							mb: 4,
						}}
					>
						{paywallModal.plans.map((plan) => (
							<Card
								key={plan.id}
								sx={{
									border: plan.highlight
										? "2px solid rgb(47, 127, 224)"
										: "1px solid rgba(0,0,0,0.12)",
									borderRadius: 3,
									position: "relative",
									transition: "transform 0.2s",
									"&:hover": {
										transform: "translateY(-2px)",
									},
								}}
							>
								{plan.badge && (
									<Chip
										label={getText(plan.badge.text, currentVariant)}
										size="small"
										sx={{
											position: "absolute",
											top: 12,
											right: 12,
											backgroundColor: "rgb(47, 127, 224)",
											color: "white",
											fontWeight: 600,
										}}
									/>
								)}

								<CardContent sx={{ p: 3 }}>
									<Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
										{plan.name}
									</Typography>
									<Box sx={{ display: "flex", alignItems: "baseline", mb: 1, flexWrap: "wrap", gap: 0.5 }}>
										{plan.originalPrice && (
											<Typography variant="h6" sx={{ fontWeight: 600, textDecoration: "line-through", opacity: 0.4 }}>
												{plan.originalPrice}
											</Typography>
										)}
										<Typography variant="h4" sx={{ fontWeight: 700 }}>
											{plan.price}
										</Typography>
										<Typography
											variant="body2"
											color="text.secondary"
											sx={{ ml: 1 }}
										>
											/ {plan.period}
										</Typography>
									</Box>
									<Typography
										variant="body2"
										color="text.secondary"
										sx={{ mb: 2, minHeight: 40 }}
									>
										{getText(plan.description, currentVariant)}
									</Typography>

									<Box component="ul" sx={{ pl: 2, mb: 3, listStyle: "none" }}>
										{plan.features.map((feature, index) => (
											<Box
												key={index}
												component="li"
												sx={{
													display: "flex",
													alignItems: "flex-start",
													mb: 1,
												}}
											>
												<Box
													sx={{
														width: 16,
														height: 16,
														borderRadius: "50%",
														backgroundColor: "rgb(47, 127, 224)",
														display: "flex",
														alignItems: "center",
														justifyContent: "center",
														mr: 1,
														mt: 0.25,
														flexShrink: 0,
													}}
												>
													<Typography
														sx={{
															color: "white",
															fontSize: 10,
															fontWeight: "bold",
														}}
													>
														✓
													</Typography>
												</Box>
												<Typography variant="body2">{feature}</Typography>
											</Box>
										))}
									</Box>

									<Button
										variant={plan.highlight ? "contained" : "outlined"}
										fullWidth
										sx={{
											py: 1.5,
											borderRadius: 2,
											textTransform: "none",
											fontWeight: 600,
											backgroundColor: plan.highlight
												? "rgb(47, 127, 224)"
												: "transparent",
											borderColor: "rgb(47, 127, 224)",
											color: plan.highlight ? "white" : "rgb(47, 127, 224)",
											"&:hover": {
												backgroundColor: plan.highlight
													? "rgb(47, 127, 224)"
													: "rgba(47, 127, 224,0.1)",
												borderColor: "rgb(47, 127, 224)",
											},
										}}
										onClick={() => {
											console.log(`Comprar plan: ${plan.id}`);
										}}
									>
										{plan.highlight ? "Elegir este plan" : "Ver detalles"}
									</Button>
								</CardContent>
							</Card>
						))}
					</Box>

					{/* Features Comparison (Collapsible) */}
					<Box
						sx={{
							border: "1px solid rgba(0,0,0,0.12)",
							borderRadius: 2,
							p: 2,
							mb: 3,
						}}
					>
						<Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
							{getText(paywallModal.featuresComparison.title, currentVariant)}
						</Typography>
						<Box
							component="table"
							sx={{ width: "100%", borderCollapse: "collapse" }}
						>
							<Box component="thead">
								<Box component="tr">
									<Box
										component="th"
										sx={{
											textAlign: "left",
											p: 1,
											borderBottom: "1px solid rgba(0,0,0,0.12)",
											fontWeight: 600,
										}}
									>
										Característica
									</Box>
									<Box
										component="th"
										sx={{
											textAlign: "center",
											p: 1,
											borderBottom: "1px solid rgba(0,0,0,0.12)",
											fontWeight: 600,
										}}
									>
										Gratis
									</Box>
									<Box
										component="th"
										sx={{
											textAlign: "center",
											p: 1,
											borderBottom: "1px solid rgba(0,0,0,0.12)",
											fontWeight: 600,
										}}
									>
										Personaliza
									</Box>
									<Box
										component="th"
										sx={{
											textAlign: "center",
											p: 1,
											borderBottom: "1px solid rgba(0,0,0,0.12)",
											fontWeight: 600,
										}}
									>
										Viajero
									</Box>
								</Box>
							</Box>
							<Box component="tbody">
								{paywallModal.featuresComparison.features.map(
									(feature, index) => (
										<Box component="tr" key={index}>
											<Box
												component="td"
												sx={{
													p: 1.5,
													borderBottom: "1px solid rgba(0,0,0,0.08)",
												}}
											>
												<Typography variant="body2">
													{getText(feature.name, currentVariant)}
												</Typography>
											</Box>
											<Box
												component="td"
												sx={{
													textAlign: "center",
													p: 1.5,
													borderBottom: "1px solid rgba(0,0,0,0.08)",
												}}
											>
												{typeof feature.free === "boolean" ? (
													feature.free ? (
														<Typography sx={{ color: "rgb(47, 127, 224)", fontSize: 20 }}>
															✓
														</Typography>
													) : (
														<Typography sx={{ color: "#ccc", fontSize: 20 }}>
															✕
														</Typography>
													)
												) : (
													<Typography variant="body2">
														{feature.free}
													</Typography>
												)}
											</Box>
											<Box
												component="td"
												sx={{
													textAlign: "center",
													p: 1.5,
													borderBottom: "1px solid rgba(0,0,0,0.08)",
												}}
											>
												{typeof feature.oneTime === "boolean" ? (
													feature.oneTime ? (
														<Typography sx={{ color: "rgb(47, 127, 224)", fontSize: 20 }}>
															✓
														</Typography>
													) : (
														<Typography sx={{ color: "#ccc", fontSize: 20 }}>
															✕
														</Typography>
													)
												) : (
													<Typography variant="body2">
														{feature.oneTime}
													</Typography>
												)}
											</Box>
											<Box
												component="td"
												sx={{
													textAlign: "center",
													p: 1.5,
													borderBottom: "1px solid rgba(0,0,0,0.08)",
												}}
											>
												{typeof feature.annual === "boolean" ? (
													feature.annual ? (
														<Typography sx={{ color: "rgb(47, 127, 224)", fontSize: 20 }}>
															✓
														</Typography>
													) : (
														<Typography sx={{ color: "#ccc", fontSize: 20 }}>
															✕
														</Typography>
													)
												) : (
													<Typography variant="body2">
														{feature.annual}
													</Typography>
												)}
											</Box>
										</Box>
									)
								)}
							</Box>
						</Box>
					</Box>

					{/* Close Button */}
					<Button
						variant="text"
						fullWidth
						onClick={onClose}
						sx={{
							textTransform: "none",
							color: "text.secondary",
						}}
					>
						{getText(paywallModal.closeButton.text, currentVariant)}
					</Button>
				</Box>
			</Box>
		</Modal>
	);
};

export default PaywallModal;
